import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { gamificationAPI } from '../lib/api';
import {
  Trophy,
  Medal,
  Crown,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';

const rankIcons = {
  1: Crown,
  2: Medal,
  3: Trophy,
};

const rankColors = {
  1: 'text-amber-500',
  2: 'text-gray-400',
  3: 'text-amber-700',
};

export const Leaderboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await gamificationAPI.getLeaderboard();
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  const topThree = data?.leaderboard?.slice(0, 3) || [];
  const restOfLeaderboard = data?.leaderboard?.slice(3) || [];

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Trophy className="w-8 h-8 text-amber-500" />
              {t('leaderboard.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('leaderboard.topPerformers')}
            </p>
          </div>
          {data?.current_user_rank && (
            <Card className="p-4 bg-primary/5 border-primary/20">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('leaderboard.yourPosition')}</p>
                  <p className="text-2xl font-bold">#{data.current_user_rank}</p>
                </div>
                <div className="ml-4">
                  <p className="text-sm text-muted-foreground">{data.total_users}</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Top 3 Podium */}
        {topThree.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* Second Place */}
            {topThree[1] && (
              <div className="flex flex-col items-center pt-8" data-testid="rank-2">
                <div className="relative">
                  <Avatar className="w-16 h-16 md:w-20 md:h-20 border-4 border-gray-400">
                    <AvatarImage src={topThree[1].avatar} />
                    <AvatarFallback className="text-lg">
                      {topThree[1].name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                </div>
                <h3 className="mt-4 font-semibold text-center line-clamp-1">{topThree[1].name}</h3>
                <div className="flex items-center gap-1 mt-1">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="font-bold text-amber-500">{topThree[1].points?.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* First Place */}
            {topThree[0] && (
              <div className="flex flex-col items-center" data-testid="rank-1">
                <Crown className="w-8 h-8 text-amber-500 mb-2 animate-float" />
                <div className="relative">
                  <Avatar className="w-20 h-20 md:w-24 md:h-24 border-4 border-amber-500 ring-4 ring-amber-500/20">
                    <AvatarImage src={topThree[0].avatar} />
                    <AvatarFallback className="text-xl bg-amber-500/10">
                      {topThree[0].name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full gold-gradient flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                </div>
                <h3 className="mt-4 font-bold text-lg text-center line-clamp-1">{topThree[0].name}</h3>
                <div className="flex items-center gap-1 mt-1">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="font-bold text-lg text-amber-500">{topThree[0].points?.toLocaleString()}</span>
                </div>
                {topThree[0].id === user?.id && (
                  <Badge className="mt-2 bg-amber-500 text-white">That's You!</Badge>
                )}
              </div>
            )}

            {/* Third Place */}
            {topThree[2] && (
              <div className="flex flex-col items-center pt-12" data-testid="rank-3">
                <div className="relative">
                  <Avatar className="w-14 h-14 md:w-16 md:h-16 border-4 border-amber-700">
                    <AvatarImage src={topThree[2].avatar} />
                    <AvatarFallback>
                      {topThree[2].name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-700 flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                </div>
                <h3 className="mt-4 font-semibold text-center line-clamp-1">{topThree[2].name}</h3>
                <div className="flex items-center gap-1 mt-1">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="font-bold text-amber-500">{topThree[2].points?.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rest of Leaderboard */}
        <Card data-testid="leaderboard-list">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {restOfLeaderboard.map((entry) => (
                <div
                  key={entry.id}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                    entry.id === user?.id
                      ? 'bg-primary/5 border border-primary/20'
                      : 'hover:bg-muted'
                  }`}
                  data-testid={`rank-${entry.rank}`}
                >
                  <div className="w-8 text-center font-bold text-lg text-muted-foreground">
                    {entry.rank}
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={entry.avatar} />
                    <AvatarFallback>{entry.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {entry.name}
                      {entry.id === user?.id && (
                        <Badge variant="outline" className="ml-2">You</Badge>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground capitalize">
                      {entry.role?.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span className="font-bold">{entry.points?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Leaderboard;
